import React from "react";
//#region redux
import { store } from "store";
import { useDispatch } from "react-redux";
import { openedCloseConfirmation } from "store/actions/diagnosticsActions";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { DiagnosticsEnums } from "models/enums";
import { SupportFormTypes } from "models/formTypes";
//#endregion
//#region components
import { Icon, RoundButton, PopperButton, useCloseConfirmation } from "components/common";
import FAQPopperContent from "./_partials/faqPopperContent";
import { useSupportService } from "serviceHooks";
import useSupportTicketCreateDialog from "./useCreateDialog";
import useFAQSupportFlowCloseConfirmation from "./useFAQSupportFlowCloseConfirmation";
import useCurrentAgentConversation from "./agent/useCurrentAgentConversation";
import { useAgentAccess } from "./agent/agentAccess";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

const SupportSpeedDial: React.FC = () => {
  const currentUserMessageRef = React.useRef<string>("");
  const isSupportStepRef = React.useRef<boolean>(false);
  const closeConfirmationOpenedRef = React.useRef(false);
  const supportFormValuesRef = React.useRef<SupportFormTypes.Create | undefined>(undefined);
  const supportService = useSupportService();
  const { getDialog: getSupportTicketCreateDialog } = useSupportTicketCreateDialog();
  const { getCloseConfirmation: getFAQSupportFlowCloseConfirmation } = useFAQSupportFlowCloseConfirmation();
  const { showCustomConfirmation } = useCloseConfirmation();
  const dispatch = useDispatch();
  const { state: agentConversation } = useCurrentAgentConversation();
  // Only the agent step clears it, so without the agent it would never go out.
  const agentEnabled = useAgentAccess();

  const onSentSupportCase = () => {
    supportService.submitFAQSupportFlowDiagnostics(DiagnosticsEnums.ExitAction.SentSupportCase);
    closeConfirmationOpenedRef.current = false;
  };

  const handleSupportFormUpdate = React.useCallback((values: SupportFormTypes.Create) => {
    supportFormValuesRef.current = values;
  }, []);

  const onClose = () => {
    if (closeConfirmationOpenedRef.current) return;

    //TODO refactor to use useSelector instead - https://app.clickup.com/t/4523827/DEV-12514
    const faqDiagnostics = store.getState().diagnostics.faqDiagnostics;

    const exitAction = faqDiagnostics.exitAction;
    if (exitAction === undefined)
      supportService.submitFAQSupportFlowDiagnostics(DiagnosticsEnums.ExitAction.ClosedDialog);
    else supportService.submitFAQSupportFlowDiagnostics();
    closeConfirmationOpenedRef.current = false;
  };

  /**
   * @deprecated This function is deprecated. Use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Replace store.getState() calls with useSelector hooks
   * 2. Use the hooks in React components
   * 3. Handle state updates through React state management
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const isSuspected = isSuspectedAccidentalClose();
   *
   * @example
   * // New way (recommended):
   * const faqDiagnostics = useSelector((state: AppState) => state.diagnostics.faqDiagnostics);
   * const isSuspectedAccidentalClose = () => {
   *   return supportService.isSuspectedAccidentalClose(faqDiagnostics);
   * };
   */
  const isSuspectedAccidentalClose = () => {
    //TODO refactor to use useSelector instead - https://app.clickup.com/t/4523827/DEV-12514
    const faqDiagnostics = store.getState().diagnostics.faqDiagnostics;
    return supportService.isSuspectedAccidentalClose(faqDiagnostics);
  };

  const overrideCloseMenu = (closeMenu: () => void) => {
    if (closeConfirmationOpenedRef.current) return;

    const closeHandler = () => {
      closeConfirmationOpenedRef.current = false;
      isSupportStepRef.current = false;
      supportFormValuesRef.current = undefined;
      return closeMenu();
    };

    if (!isSuspectedAccidentalClose()) {
      return closeHandler();
    }

    if (isSupportStepRef.current) {
      if (!supportService.supportFormHasContent(supportFormValuesRef.current)) {
        return closeHandler();
      }

      closeConfirmationOpenedRef.current = true;
      showCustomConfirmation(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FormExitWarningMessage),
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.UnansweredQuestionTextSupportStep),
        async () => {
          closeHandler();
        },
        async () => {
          closeConfirmationOpenedRef.current = false;
        }
      );
      return;
    }

    // FAQ step: user searched but didn't click any suggestion — offer to create
    // a case instead of just closing. Shown only when isSuspectedAccidentalClose
    // says so (typed a question, got suggestions, didn't engage). Figma: section
    // 11768:19841, "Om man stänger ner efter att ha fått förslag".
    const supportTicketDialog = getSupportTicketCreateDialog(
      onSentSupportCase,
      () => supportService.submitFAQSupportFlowDiagnostics(DiagnosticsEnums.ExitAction.ClosedDialog),
      currentUserMessageRef.current,
      true
    );
    const { showConfirmation } = getFAQSupportFlowCloseConfirmation(supportTicketDialog);

    closeConfirmationOpenedRef.current = true;
    dispatch(openedCloseConfirmation());
    showConfirmation(
      async () => {
        closeHandler();
      },
      async () => {
        closeMenu();
        closeConfirmationOpenedRef.current = false;
      },
      async () => {
        closeConfirmationOpenedRef.current = false;
      }
    );
  };

  return (
    <PopperButton
      button={
        <RoundButton size="lg" hoverText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Help)}>
          <Icon icon={IconDefinitionConstants.WCommentDots} />
          {agentEnabled && agentConversation.unread && (
            <span className={`${cssExports.unreadIndicator} ${cssExports.speedDialUnreadIndicator}`} />
          )}
        </RoundButton>
      }
      contentOnOpen={<Icon icon={IconDefinitionConstants.WPlus} />}
      classes={{
        root: cssExports.speedDialOuter,
        menuOpen: cssExports.speedDialOpen,
        menuClosing: cssExports.speedDialClosing,
      }}
      menuPlacement={"top"}
      blurBackgroundOnOpen
      onClose={onClose}
      blockOutsideClick={closeConfirmationOpenedRef.current}
      overrideCloseMenu={overrideCloseMenu}
    >
      {(closeMenu) => (
        <FAQPopperContent
          closePopper={closeMenu}
          setSubmitMessage={(currentMessage: string) => (currentUserMessageRef.current = currentMessage)}
          setIsSupportStep={(isSupportStep: boolean) => (isSupportStepRef.current = isSupportStep)}
          onSupportFormUpdate={handleSupportFormUpdate}
        />
      )}
    </PopperButton>
  );
};

export default SupportSpeedDial;
